import React, { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import { MdClose } from "react-icons/md";
import DateProviderPrompt from "./DateProviderPrompt";
import NotCompletedPrompt from "./NotCompletedPrompt";
import PromptButtons from "./PromptButtons";
import NotConfirmedPrompt from "./NotConfirmedPrompt";
import ConfirmationPrompt from "./ConfirmationPrompt";
import CprDateInfo from "../CprDateInfo";
import FormPaper from "../../../layout/FormPaper";
import ConsentPrompt from "./ConsentPrompt";
import Flash from "../../../layout/Flash";
import * as CONST from "../../../Constants";
import * as API from "../../../api";
import { PatientData, FeedPrompt } from "../../../Contexts";
import { useStyles } from "../../../layout/Styles";

const propTypes = {
  cpr: PropTypes.object.isRequired,
  updateProgress: PropTypes.func.isRequired,
  promptActive: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  flash: PropTypes.func,
  state: PropTypes.object,
};

const StatusPrompts = ({ dispatch, cpr, updateProgress, promptActive, flash, state }) => {
  const feedPrompt = React.useContext(FeedPrompt);
  const patient = React.useContext(PatientData);
  const [prompt, setPrompt] = useState({
    show: false,
    selected: null,
  });
  const [updated, setUpdated] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);
  const [editAppointment, setEditAppointment] = useState(false);
  const [hide, setHide] = useState(false);
  const styles = useStyles();

  const postResponse = async (req) => {
    const res = await API.postCprStatus(req);
    if (res.error) {
      showFlashMessage({ error: res.error });
    } else {
      dispatchAction(res.data.cpr);
      showFlashMessage({
        success:
          req.option_value == 6
            ? CONST.ORDER_REQUESTED_TEXT
            : req.option_value == 1
            ? cpr.status == "reminder"
              ? CONST.FLASH.REMINDER_CONFIRMED
              : !cpr.appointment_date
              ? CONST.FLASH.DUE_DATE_CONFIRMED
              : CONST.FLASH.APPOINTMENT_CONFIRMED
            : CONST.FLASH.RESPONDED,
      });
    }
  };

  React.useEffect(() => {
    setPrompt({ show: false, selected: null });
    setEditAppointment(false);

    return () => {
      setPrompt({ show: false, selected: null });
      setEditAppointment(false);
      setFlashMessage(null);
    };
  }, [cpr]);

  function showFlashMessage(m) {
    if (flashMessage) {
      setFlashMessage(null);
      setTimeout(() => {
        setFlashMessage(m);
        console.log("StatusPrompts[showFlashMessage]");
        flash(m);
      }, 250);
    } else {
      setFlashMessage(m);
      flash(m);
    }
  }

  function updateCpr(updated) {
    dispatch({ type: "set_current_cpr", payload: updated });
    updateProgress(updated);
    setPrompt({ show: false, selected: null });
    setEditAppointment(false);
    promptActive();
  }

  function dispatchAction(payload) {
    if (payload.type && payload.type == "extension") {
      return updateCpr({ ...payload, updatedDueDate: true });
    }
    if (payload.type && payload.type == "scheduled") {
      return updateCpr({ ...payload, updatedAppointmentDate: true });
    }
    if (!payload.type || (payload.type && payload.type !== ("extension" || "scheduled"))) {
      return updateCpr({ ...payload, updatedDueDate: false, updatedAppointmentDate: false });
    }
  }

  const onPromptSelected = (val) => {
    if (
      cpr.status == "reminder" &&
      val == "no" &&
      cpr.patient_requested_order &&
      !cpr.appointment_date
    ) {
      postResponse({ cpr_id: cpr.id, option_value: 10 });
    }
    editAppointment && setEditAppointment(false);
    setPrompt(val);
    !hide ? setHide(true) : setHide(false);
    promptActive();
  };

  function CancelPromptButton(props) {
    return (
      <Box mx={0.5}>
        <button
          className="prompt cancel"
          onClick={(e) => {
            e.preventDefault();
            props.clickHandler();
          }}
        >
          <MdClose className={styles.cancelIconBtn} />
          Cancel
        </button>
      </Box>
    );
  }

  function ActionButtons() {
    return (
      <>
        {!editAppointment && !prompt.show ? (
          <Grid container px={1.5} pt={1} spacing={1}>
            {!prompt.selected &&
              cpr.sub_status !== "patient_reported_completed" &&
              ((cpr.status == "reminder" && cpr.appointment_date) ||
                cpr.updatedAppointmentDate ||
                cpr.updatedDueDate ||
                (cpr.status == "confirmation" && cpr.appointment_date)) && (
                <Grid
                  item
                  md={cpr.updatedDueDate || cpr.updatedAppointmentDate ? 12 : 6}
                  xs={12}
                  pb={1.5}
                >
                  <button
                    onClick={(e) => {
                      promptActive();
                      !editAppointment && setEditAppointment(true);
                    }}
                    className="prompt secondary"
                  >
                    {cpr.appointment_date ? "Edit Appointment" : "Edit Due Date"}
                  </button>
                </Grid>
              )}
            {!prompt.selected &&
              cpr.status !== "closed" &&
              !cpr.cpr_type_response_status &&
              !cpr.updatedDueDate &&
              !cpr.updatedAppointmentDate && (
                <Grid
                  item
                  xs={12}
                  pb={1.5}
                  md={!cpr.appointment_date && !cpr.updatedDueDate ? 12 : 6}
                >
                  <button
                    className="prompt secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      onPromptSelected({ ...prompt, show: true });
                    }}
                  >
                    {CONST.BUTTON_TEXT.COMPLETED}
                  </button>
                </Grid>
              )}
          </Grid>
        ) : (
          <CancelPromptButton
            clickHandler={() => {
              if (editAppointment) {
                setEditAppointment(false);
              } else {
                onPromptSelected({ show: false, selected: null });
                prompt.show && !prompt.selected
                  ? onPromptSelected({ ...prompt, show: false })
                  : onPromptSelected({ ...prompt, show: true });
              }
            }}
          />
        )}
      </>
    );
  }

  function Prompt({ prompt }) {
    return (
      <FormPaper>
        <DateProviderPrompt
          {...prompt}
          cpr={cpr}
          dispatch={(payload) => {
            dispatchAction(payload);
          }}
          handleUpdated={() => (!updated ? setUpdated(true) : null)}
          handleFlash={(msg) => showFlashMessage(msg)}
        />
      </FormPaper>
    );
  }
  const FollowUpPastDue = () => {
    return (
      <>
        <CprDateInfo
          cpr={cpr}
          promptText={
            !cpr.patient_response_status &&
            cpr.sub_status !== "patient_reported_completed" &&
            !cpr.in_review
              ? CONST.DID_COMPLETE.prompt
              : ""
          }
        />
        {!cpr.patient_response_status && (
          <>
            {!cpr.in_review && (
              <>
                {!prompt.selected && (
                  <PromptButtons
                    promptText={""}
                    buttons={{
                      yesText: CONST.DID_COMPLETE.options.yes,
                      noText: CONST.DID_COMPLETE.options.no,
                      onSelect: (val) => onPromptSelected({ ...prompt, selected: val }),
                    }}
                  />
                )}

                {prompt.selected && (
                  <>
                    <CancelPromptButton
                      clickHandler={() => onPromptSelected({ show: false, selected: null })}
                    />

                    {prompt.selected == "yes" ? (
                      <Prompt prompt={CONST.DATE_PROVIDER_PROMPTS.COMPLETED} />
                    ) : (
                      <NotCompletedPrompt
                        patient={patient}
                        cpr={cpr}
                        dispatch={(payload) => {
                          dispatchAction(payload);
                        }}
                        handleUpdated={() => (!updated ? setUpdated(true) : null)}
                        handleFlash={(msg) => showFlashMessage(msg)}
                      />
                    )}
                  </>
                )}
              </>
            )}

            {!editAppointment &&
              cpr.sub_status !== "patient_reported_completed" &&
              cpr.in_review &&
              (!cpr.updatedDueDate || !cpr.updatedAppointmentDate) &&
              !updated && (
                <>
                  <ActionButtons />
                  {prompt.show && <Prompt prompt={CONST.DATE_PROVIDER_PROMPTS.COMPLETED} />}
                </>
              )}
          </>
        )}
      </>
    );
  };

  const ConfirmationReminder = () => {
    const status = { ...CONST.CONFIRM_CONFIRMATION_PROMPT(cpr.date_type) };
    const [promptConfirm, onPromptConfirm] = useState({ selected: null });

    const Confirmation = () => {
      function DueDate() {
        if (cpr.updatedDueDate) {
          return (
            <ConfirmationPrompt
              cpr={cpr}
              state={state}
              dispatch={(payload) => dispatchAction(payload)}
              handleUpdated={() => (!updated ? setUpdated(true) : null)}
              handleFlash={(msg) => showFlashMessage(msg)}
            />
          );
        } else {
          let appointmentPrompt = cpr && cpr.primary == true ? CONST.APPOINTMENT.prompt : CONST.APPOINTMENT.nonprimary_prompt;
          return (
            <>
              {!promptConfirm.selected && (
                <FormPaper>
                  <PromptButtons
                    promptText={appointmentPrompt}
                    buttons={{
                      yesText: status.options.yes,
                      noText: status.options.no,
                      onSelect: (val) => {
                        if (val == "no" && cpr.order_document && cpr.order_document.url) {
                          postResponse({ cpr_id: cpr.id, option_value: 1 });
                        } else {
                          onPromptConfirm({ ...promptConfirm, selected: val });
                        }
                      },
                    }}
                  />
                </FormPaper>
              )}
              {promptConfirm.selected == "yes" && (
                <Prompt prompt={CONST.DATE_PROVIDER_PROMPTS.SCHEDULED} />
              )}

              {promptConfirm.selected == "no" && (
                <ConfirmationPrompt
                  cpr={cpr}
                  state={state}
                  dispatch={(payload) => dispatchAction(payload)}
                  handleUpdated={() => (!updated ? setUpdated(true) : null)}
                  handleFlash={(msg) => showFlashMessage(msg)}
                />
              )}
            </>
          );
        }
      }
      function AppointmentDate() {
        return patient.patient_consented_to_notifications ? (
          <ConfirmationPrompt
            cpr={cpr}
            dispatch={(payload) => dispatchAction(payload)}
            handleUpdated={() => (!updated ? setUpdated(true) : null)}
            handleFlash={(msg) => showFlashMessage(msg)}
          />
        ) : (
          <ConsentPrompt
            cpr={cpr}
            handleUpdated={() => (!updated ? setUpdated(true) : null)}
            handlePatientConsented={(payload) => {
              dispatch({
                type: "set_patient_consented",
                payload: payload,
              });
            }}
            dispatch={(payload) => {
              dispatchAction(payload);
            }}
          />
        );
      }

      function renderInitPrompt() {
        return (
          <>
            <PromptButtons
              promptText={""}
              buttons={{
                yesText: cpr.updatedDueDate ? "Confirm" : status.options.yes,
                noText: cpr.updatedDueDate ? "Do Not Confirm" : status.options.no,
                onSelect: (val) => {
                  if (cpr.appointment_date && val == "yes") {
                    postResponse({ cpr_id: cpr.id, option_value: 1 });
                  } else {
                    onPromptSelected({ ...prompt, selected: val });
                  }
                },
              }}
              hideNoButton={false}
            />
          </>
        );
      }

      return (
        <>
          {!prompt.show && !editAppointment && !cpr.cpr_type_response_status && (
            <>
              {!prompt.selected && renderInitPrompt()}
              {prompt.selected == "yes" && cpr.appointment_date && <AppointmentDate />}
              {prompt.selected == "yes" && !cpr.appointment_date && <DueDate />}

              {prompt.selected == "no" && (
                <NotConfirmedPrompt
                  cpr={cpr}
                  state={state}
                  dispatch={(payload) => {
                    dispatchAction(payload);
                  }}
                  handleUpdated={() => (!updated ? setUpdated(true) : null)}
                  handleFlash={(msg) => showFlashMessage(msg)}
                />
              )}
            </>
          )}
        </>
      );
    };

    return (
      <>
        {cpr.cpr_type_response_status &&
          cpr.cpr_type_response_status !== "Rejected" &&
          cpr.sub_status !== "patient_reported_completed" &&
          (!cpr.primary_code || patient.out_of_area) && (
            <Box m={1.5}>
              <Alert severity="error">{CONST.STATUS_MESSAGE.ALERT_FOR_RESULTS}</Alert>
            </Box>
          )}
        <CprDateInfo
          cpr={cpr}
          promptText={
            cpr.updatedDueDate && !cpr.cpr_type_response_status
              ? CONST.CONFIRM_UPDATED_DUE_DATE
              : cpr.updatedAppointmentDate && !cpr.cpr_type_response_status
              ? CONST.CONFIRM_UPDATED_APPOINTMENT_DATE
              : cpr.cpr_type_response_status
              ? null
              : !cpr.cpr_type_response_status && cpr.sub_status !== "patient_reported_completed"
              ? status.prompt
              : null
          }
        />
        {cpr.sub_status !== "patient_reported_completed" && (
          <>
            {prompt.selected && (
              <CancelPromptButton
                clickHandler={() => {
                  promptConfirm.selected && onPromptConfirm({ selected: null });
                  prompt.selected && onPromptSelected({ show: false, selected: null });
                }}
              />
            )}
            {!cpr.cpr_type_response_status && <Confirmation />}
            <ActionButtons />
            {editAppointment && (
              <Prompt
                prompt={
                  cpr.appointment_date
                    ? CONST.DATE_PROVIDER_PROMPTS.SCHEDULED
                    : CONST.DATE_PROVIDER_PROMPTS.DUE_DATE
                }
              />
            )}
            {!prompt.selected && prompt.show && (
              <Prompt prompt={CONST.DATE_PROVIDER_PROMPTS.COMPLETED} />
            )}
          </>
        )}
      </>
    );
  };

  const renderNeedsResponsePrompt = (
    <div className="bt">
      {(cpr.status == "past_due" || cpr.status == "follow_up") && <FollowUpPastDue />}
      {(cpr.status == "confirmation" || cpr.status == "reminder") && <ConfirmationReminder />}
    </div>
  );

  const renderPrompt = (
    <>
      {flashMessage && (
        <Flash flash={flashMessage} handleClose={() => setFlashMessage(null)} showButton={false} />
      )}

      {feedPrompt.type == "completed" && <Prompt prompt={CONST.DATE_PROVIDER_PROMPTS.COMPLETED} />}
      {feedPrompt.type == "scheduled" && !cpr.updatedAppointmentDate && !cpr.updatedDueDate && (
        <Prompt prompt={CONST.DATE_PROVIDER_PROMPTS.SCHEDULED} />
      )}
      {feedPrompt.type == "scheduled" && (cpr.updatedAppointmentDate || cpr.updatedDueDate) && (
        <ConfirmationReminder />
      )}
    </>
  );

  return (
    <>{location.pathname.includes("notifications") ? renderNeedsResponsePrompt : renderPrompt}</>
  );
};

StatusPrompts.prototype = propTypes;
export default StatusPrompts;
